/**
 * Search API service for global search across modules.
 * @module lib/api/search
 */

import { apiClient, emptyResponseToNull } from './api-client'
import { buildQueryString } from './base-api'
import { createSingletonApi } from './singleton-factory'

export type LinkableModuleType =
  | 'ENTITY'
  | 'PROJECT'
  | 'TASK_GROUP'
  | 'TASK'
  | 'TICKET'
  | 'DEAL'
  | 'LEAD'
  | 'CONTACT'
  | 'EVENT'
  | 'CONTRACT'
  | 'INVOICE'
  | 'INVENTORY'
  | 'SUPPLIER'
  | 'DOCUMENT'
  | 'NOTE'
  | 'CUSTOM_COLLECTION'
  | 'ORGANIZATION'
  | 'KNOWLEDGE_BASE'

export interface ModuleSearchResult {
  id: string
  name: string
  identifier?: string
  type: LinkableModuleType
  parentName?: string
  fullPath?: string
  relevanceScore: number
  entityId?: string
  projectId?: string
  groupColor?: string
  collectionId?: string
  collectionSlug?: string
  collectionName?: string
}

export interface SearchModulesParams {
  q: string
  limit?: number
  types?: string[] // Filter by module types (e.g., ['ENTITY', 'PROJECT'])
  preferredType?: LinkableModuleType // Optional type to boost in ranking (does not hard-filter)
  collectionId?: string // Filter custom collection results by collection ID
  parentType?: LinkableModuleType // Optional scoped search root type
  parentId?: string // Optional scoped search root id
  excludePrivateNotes?: boolean // Restrict NOTE results to shared/team/record notes
}

const RECENT_ITEMS_CACHE_TTL_MS = 60_000

const recentItemsCache = new Map<
  string,
  { expiresAt: number; items: ModuleSearchResult[] }
>()
const recentItemsInFlight = new Map<string, Promise<ModuleSearchResult[]>>()

class SearchApiService {
  /**
   * Searches across all modules
   * @param params - Search parameters including query string and optional limit
   * @returns Array of search results
   */
  async searchModules(
    params: SearchModulesParams,
  ): Promise<ModuleSearchResult[]> {
    // Convert types array to comma-separated string for the API
    const apiParams: Record<string, string | number | undefined> = {
      q: params.q,
      limit: params.limit,
      preferredType: params.preferredType,
      collectionId: params.collectionId,
      parentType: params.parentType,
      parentId: params.parentId,
      excludePrivateNotes: params.excludePrivateNotes ? 'true' : undefined,
    }
    if (params.types && params.types.length > 0) {
      apiParams.types = params.types.join(',')
    }
    const queryString = buildQueryString(apiParams)
    return apiClient<ModuleSearchResult[]>(`/search/modules${queryString}`)
  }

  /**
   * Gets a specific module by type and ID
   * @param type - Module type
   * @param id - Module ID
   * @returns The module search result or null
   */
  async getModuleById(
    type: string,
    id: string,
    params: { excludePrivateNotes?: boolean } = {},
  ): Promise<ModuleSearchResult | null> {
    const queryString = buildQueryString({
      id,
      type,
      excludePrivateNotes: params.excludePrivateNotes ? 'true' : undefined,
    })
    return emptyResponseToNull(
      await apiClient<ModuleSearchResult | null>(`/search/module${queryString}`),
    )
  }

  /**
   * Gets tasks that are due soon
   * @param limit - Optional limit on number of results
   * @returns Array of tasks due soon
   */
  async getTasksDueSoon(limit?: number): Promise<ModuleSearchResult[]> {
    const queryString = limit ? buildQueryString({ limit }) : ''
    return apiClient<ModuleSearchResult[]>(
      `/search/tasks/due-soon${queryString}`,
    )
  }

  /**
   * Gets recent items across modules, respecting type filters
   * @param params - Parameters including limit, types filter, and collectionId
   * @returns Array of recent items
   */
  async getRecentItems(
    params: {
      limit?: number
      types?: string[]
      collectionId?: string
      parentType?: LinkableModuleType
      parentId?: string
      excludePrivateNotes?: boolean
    } = {},
  ): Promise<ModuleSearchResult[]> {
    const apiParams: Record<string, string | number | undefined> = {
      limit: params.limit,
      collectionId: params.collectionId,
      parentType: params.parentType,
      parentId: params.parentId,
      excludePrivateNotes: params.excludePrivateNotes ? 'true' : undefined,
    }
    if (params.types && params.types.length > 0) {
      apiParams.types = params.types.join(',')
    }
    const queryString = buildQueryString(apiParams)
    const cacheKey = queryString || '?'
    const now = Date.now()
    const cached = recentItemsCache.get(cacheKey)
    if (cached && cached.expiresAt > now) {
      return cached.items
    }

    const inFlight = recentItemsInFlight.get(cacheKey)
    if (inFlight) {
      return inFlight
    }

    const request = apiClient<ModuleSearchResult[]>(
      `/search/recent${queryString}`,
    )
      .then(items => {
        recentItemsCache.set(cacheKey, {
          items,
          expiresAt: Date.now() + RECENT_ITEMS_CACHE_TTL_MS,
        })
        return items
      })
      .finally(() => {
        recentItemsInFlight.delete(cacheKey)
      })

    recentItemsInFlight.set(cacheKey, request)
    return request
  }
}

export const searchApi = createSingletonApi(SearchApiService)
